/**
 * AppContext.js — Global State Manager (MVP — No Backend)
 *
 * This is the "brain" of the app. Every component can access this state via:
 *   const { user, allTransactions, ... } = useAppContext();
 *
 * What it manages:
 *   - User state (guest-only, no real auth in MVP)
 *   - Transaction data (from Dexie/IndexedDB, all on-device)
 *   - PDF file processing (parsers for PhonePe, GPay, Paytm)
 *   - Filters (month, source, date range, type)
 *   - Tab navigation (home, parser, analytics, budgets, goals, etc.)
 *   - Sample data loading (for demo mode)
 *
 * Data flow (MVP):
 *   1. User uploads PDF → parsed in browser → saved to IndexedDB
 *   2. Everything stays on-device. No server calls.
 *   3. Retags → saved locally only
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { parsePhonePeText, parseGPayText, parsePaytmText, parseDateOnly, getFreeHistoryCutoff } from '../utils/parsers';
import { categorizeMerchant, normalizeMerchant, recategorizeStoredTransactions } from '../utils/categorization';
import { db } from '../utils/db';
import { handleRetag } from '../utils/sync';
import { useLiveQuery } from 'dexie-react-hooks';
import { SAMPLE_TRANSACTIONS, SAMPLE_FIXED_EXPENSES, SAMPLE_BUDGETS, SAMPLE_GOALS, SAMPLE_INCOME } from '../data/sampleData';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import { resetScrollLock } from '../utils/useModalA11y';
import { isProEntitled, refreshEntitlement, getProAccount } from '../utils/entitlement';

// PDF.js worker — loaded from CDN so we don't need to bundle it
const PDF_WORKER_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;

/** Warm the pdf.js worker so the first drop parses faster */
export function preloadPdfWorker() {
    try {
        if (typeof window === 'undefined') return;
        if (window.__spenderlyPdfPreloaded) return;
        window.__spenderlyPdfPreloaded = true;
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'script';
        link.href = PDF_WORKER_SRC;
        document.head.appendChild(link);
        // Touch getDocument with empty abort to init worker pipeline early
        pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
    } catch (e) {
        console.warn('PDF worker preload skipped', e);
    }
}

// ── Create the context ──
const AppContext = createContext();

/**
 * Hook to use the app context in any component.
 * Usage: const { user, allTransactions, handleFile } = useAppContext();
 */
export function useAppContext() {
    return useContext(AppContext);
}


export function AppProvider({ children }) {
    // ── UI State ──
    const [activeTab, setActiveTab] = useState('home');
    const [theme, setTheme] = useState('dark');
    const [status, setStatus] = useState({ active: false, text: '', type: '' });
    const [globalMonthFilter, setGlobalMonthFilter] = useState('all');
    const [filters, setFilters] = useState({ source: 'all', fromDate: '', toDate: '', type: 'all', merchantQuery: '' });
    const [passwordRequest, setPasswordRequest] = useState(null);
    const [lastUploadStats, setLastUploadStats] = useState(null);
    const [appScreen, setAppScreen] = useState('auto'); // auto | reality | targets | app
    const [spendFocusCategory, setSpendFocusCategory] = useState(null);

    // ── Auth State (guest-only in MVP) ──
    const [user, setUser] = useState(null);

    // ── Sample Mode (purely in-memory — never touches IndexedDB) ──
    const [isSampleMode, setIsSampleMode] = useState(false);
    const [sampleTxns, setSampleTxns] = useState([]);
    const [sampleFixedExpenses, setSampleFixedExpenses] = useState([]);
    const [sampleBudgets, setSampleBudgets] = useState([]);
    const [sampleGoals, setSampleGoals] = useState([]);
    const [sampleIncome, setSampleIncome] = useState(null);
    // Modal flag for when a write is attempted in sample mode
    const [showSampleLocked, setShowSampleLocked] = useState(false);
    const triggerSampleLocked = () => setShowSampleLocked(true);

    // ── Real Transaction Data (live from IndexedDB) ──
    const dbTransactions = useLiveQuery(
        () => db.transactions.orderBy('date').reverse().toArray(),
        []
    ) || [];

    // ── Exposed allTransactions: sample in-memory OR real IndexedDB ──
    const allTransactions = isSampleMode ? sampleTxns : dbTransactions;


    // ═══════════════════════════════════════════
    //  ON APP LOAD — Restore auth from localStorage
    // ═══════════════════════════════════════════
    useEffect(() => {
        // Clear stuck modal scroll locks from prior sessions / HMR
        resetScrollLock();

        preloadPdfWorker();
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
            try {
                const parsed = JSON.parse(savedUser);
                setUser(parsed);
                // If they were in demo mode when they last closed, restore in-memory sample data
                if (parsed.id === 'demo-user') {
                    setIsSampleMode(true);
                    setSampleTxns(SAMPLE_TRANSACTIONS.map(t => ({ ...t, user_id: 'demo-user' })));
                    setSampleFixedExpenses(SAMPLE_FIXED_EXPENSES);
                    setSampleBudgets(SAMPLE_BUDGETS);
                    setSampleGoals(SAMPLE_GOALS);
                    setSampleIncome(SAMPLE_INCOME);
                    setAppScreen('app');
                }
            } catch (e) {
                localStorage.removeItem('user');
            }
        }
    }, []);

    // Silent Pro license refresh when online
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                await refreshEntitlement();
                if (cancelled) return;
                const account = getProAccount();
                if (account?.plan === 'pro') {
                    setUser((prev) => {
                        if (!prev) return prev;
                        if (prev.plan === 'pro') return prev;
                        const next = { ...prev, plan: 'pro', email: prev.email || account.email, name: prev.name || account.name };
                        try { localStorage.setItem('user', JSON.stringify(next)); } catch { /* ignore */ }
                        return next;
                    });
                }
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, []);

    // Re-tag auto-categorized rows with improved rules (never overwrite user retags)
    useEffect(() => {
        if (isSampleMode) return undefined;
        let cancelled = false;
        const flagKey = 'spenderly_recat_v5';
        if (localStorage.getItem(flagKey) === '1') return undefined;

        (async () => {
            try {
                // Migrate legacy retag keys written as "anonymous"
                const allOverrides = await db.user_overrides.toArray();
                for (const row of allOverrides) {
                    if (row.user_id !== 'anonymous') continue;
                    await db.user_overrides.put({
                        ...row,
                        user_id: 'guest-user',
                    });
                    try {
                        await db.user_overrides.delete(['anonymous', row.merchant_key]);
                    } catch {
                        // ignore if already gone
                    }
                }

                const userId = user?.id || 'guest-user';
                const n = await recategorizeStoredTransactions(userId);
                if (!cancelled) {
                    localStorage.setItem(flagKey, '1');
                    if (n > 0) {
                        console.info(`[spenderly] Re-categorized ${n} transactions`);
                    }
                }
            } catch (err) {
                console.warn('Recategorize pass skipped', err);
            }
        })();

        return () => { cancelled = true; };
    }, [isSampleMode, user]);


    // ═══════════════════════════════════════════
    //  COMPUTED DATA — Derived from allTransactions
    // ═══════════════════════════════════════════

    /** Available months for the month filter dropdown */
    const availableMonths = React.useMemo(() => {
        const monthSet = new Set();
        for (const t of allTransactions) {
            if (t.date && t.date.length >= 7) {
                monthSet.add(t.date.substring(0, 7));
            }
        }
        return Array.from(monthSet).sort().reverse();
    }, [allTransactions]);

    /** Transactions filtered by the global month selector */
    const monthFilteredTransactions = React.useMemo(() => {
        if (globalMonthFilter === 'all') return allTransactions;
        return allTransactions.filter(t => t.date && t.date.startsWith(globalMonthFilter));
    }, [allTransactions, globalMonthFilter]);

    /** Transactions filtered by source, date range, and type */
    const filteredTransactions = React.useMemo(() => {
        return monthFilteredTransactions.filter(function (t) {
            if (filters.source !== 'all' && t.source !== filters.source) return false;
            if (filters.fromDate || filters.toDate) {
                const txnDate = parseDateOnly(t.date_time);
                if (txnDate) {
                    if (filters.fromDate) {
                        const parts = filters.fromDate.split('-');
                        const fromDate = new Date(parts[0], parts[1] - 1, parts[2]);
                        if (txnDate < fromDate) return false;
                    }
                    if (filters.toDate) {
                        const parts = filters.toDate.split('-');
                        const toDate = new Date(parts[0], parts[1] - 1, parts[2]);
                        if (txnDate > toDate) return false;
                    }
                }
            }
            if (filters.type !== 'all' && t.type !== filters.type) return false;
            if (filters.merchantQuery) {
                const q = String(filters.merchantQuery).trim().toLowerCase();
                if (q) {
                    const hay = `${t.party || ''} ${t.raw_merchant || ''} ${t.merchant_key || ''}`.toLowerCase();
                    if (!hay.includes(q)) return false;
                }
            }
            return true;
        });
    }, [monthFilteredTransactions, filters]);


    // ═══════════════════════════════════════════
    //  AUTH — Login / Logout (Guest-only in MVP)
    // ═══════════════════════════════════════════

    /**
     * Log in a user (guest mode only in MVP).
     * @param {Object} userData - { id, name, email, plan }
     */
    const handleLogin = (userData) => {
        if (!userData || typeof userData !== 'object' || !userData.id || !userData.name) {
            console.error('handleLogin called with invalid userData:', typeof userData);
            return;
        }

        const cleanUser = {
            id: String(userData.id),
            name: String(userData.name),
            email: String(userData.email || ''),
            plan: String(userData.plan || 'free'),
        };

        setUser(cleanUser);
        localStorage.setItem('user', JSON.stringify(cleanUser));
    };

    /** Log out — clear user state */
    const handleLogout = () => {
        setUser(null);
        localStorage.removeItem('user');
        setAppScreen('auto');
    };

    /** Toggle dark/light theme */
    const toggleTheme = useCallback(() => {
        setTheme(prev => {
            const newTheme = prev === 'dark' ? 'light' : 'dark';
            document.documentElement.setAttribute('data-theme', newTheme);
            return newTheme;
        });
    }, []);


    // ═══════════════════════════════════════════
    //  FILE PROCESSING — Parse PDF statements
    // ═══════════════════════════════════════════

    /**
     * Process an uploaded PDF file.
     * @param {string} source - 'phonepe', 'gpay', or 'paytm'
     * @param {File} file - The PDF file object
     * @param {boolean} isFreshStart - If true, clears existing data for this source first
     * Default mid-month re-upload merges by transaction id (bulkPut upsert).
     */
    const handleFile = useCallback((source, file, isFreshStart = false) => {
        return new Promise((resolve, reject) => {
            if (!file) {
                reject(new Error("No file provided"));
                return;
            }

            setStatus({ active: true, text: 'Processing ' + file.name + '...', type: 'loading' });

            const fileReader = new FileReader();
            fileReader.onload = function () {
                const typedarray = new Uint8Array(fileReader.result);
                const loadingTask = pdfjsLib.getDocument(typedarray);

                // Handle password-protected PDFs
                loadingTask.onPassword = function (updatePassword, reason) {
                    setPasswordRequest({ callback: updatePassword, reason });
                };

                loadingTask.promise.then(function (pdf) {
                    const numPages = pdf.numPages;
                    const textPromises = [];
                    for (let i = 1; i <= numPages; i++) {
                        textPromises.push(pdf.getPage(i).then(page => page.getTextContent()));
                    }
                    return Promise.all(textPromises);
                }).then(async function (pages) {
                    let fullText = '';
                    pages.forEach(function (page) {
                        let lastX = null, lastWidth = 0, lastY = null;

                        page.items.forEach(function (item) {
                            if (!item.str && item.str !== '') return;
                            const x = item.transform ? item.transform[4] : null;
                            const y = item.transform ? item.transform[5] : null;
                            const fontSize = item.transform ? Math.abs(item.transform[0]) : 12;
                            const itemWidth = item.width || 0;

                            if (lastY !== null && y !== null) {
                                if (Math.abs(y - lastY) > fontSize * 0.3) {
                                    fullText += ' ';
                                    lastX = null;
                                }
                            }
                            if (lastX !== null && x !== null) {
                                if (x - (lastX + lastWidth) > fontSize * 0.3) fullText += ' ';
                            } else if (lastX === null && fullText.length > 0) {
                                fullText += ' ';
                            }

                            fullText += item.str.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F\u200B\u200C\u200D\uFEFF]/g, '');
                            lastX = x; lastWidth = itemWidth; lastY = y;
                        });
                    });

                    fullText = fullText
                        .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
                        .replace(/[\u200B\u200C\u200D\uFEFF\u00A0]/g, ' ')
                        .replace(/\s{3,}/g, '  ')
                        .trim();

                    let transactions = [];
                    if (source === 'phonepe') transactions = parsePhonePeText(fullText);
                    else if (source === 'gpay') transactions = parseGPayText(fullText);
                    else if (source === 'paytm') transactions = parsePaytmText(fullText);

                    // Free: last 30 calendar days (inclusive of the day 30 days ago).
                    // Pro: full history. Never wipe Pro months on re-upload.
                    // Entitlement via signed license JWT (dev unlock only in development).
                    const allowHistory = isProEntitled(user);
                    // Pro: treat "replace source" as merge — history must not be deleted
                    const wipeSource = Boolean(isFreshStart) && !allowHistory;
                    const cutoffDate = getFreeHistoryCutoff(30);
                    const totalFound = transactions.length;

                    if (!allowHistory) {
                        const kept = [];
                        let unparsedDate = 0;
                        for (const t of transactions) {
                            const tDate = parseDateOnly(t.date_time);
                            if (!tDate) {
                                // Keep rows we couldn't date-parse — better than silently dropping
                                unparsedDate += 1;
                                kept.push(t);
                                continue;
                            }
                            if (tDate >= cutoffDate) kept.push(t);
                        }
                        transactions = kept;
                        if (unparsedDate > 0) {
                            console.warn(`[spenderly] ${unparsedDate} txns had unparseable dates; kept on Free`);
                        }
                    }

                    const skippedOlder = allowHistory ? 0 : (totalFound - transactions.length);

                    if (transactions.length === 0) {
                        setStatus({
                            active: true,
                            text: skippedOlder > 0
                                ? `All ${skippedOlder} transactions were older than ~30 days. Free tracks the recent month — upload a newer statement, or Pro keeps full history.`
                                : 'No transactions found in this PDF. Please upload a valid PhonePe, GPay, or Paytm statement.',
                            type: 'error'
                        });
                        reject(new Error("No valid transactions found"));
                        return;
                    }

                    const dbTxns = [];
                    const userId = user ? user.id : 'guest-user';
                    let autoTagged = 0;

                    // Prefetch existing rows so user/manual tags survive merge re-uploads
                    const provisionalIds = transactions.map((t) => {
                        const stableKey = t.utr_no || t.transaction_id || t.txn_id
                            || `${t.date_time}_${t.party}_${t.amount}`;
                        return String(stableKey);
                    });
                    const existingById = new Map();
                    if (provisionalIds.length) {
                        const existingRows = await db.transactions.where('id').anyOf(provisionalIds).toArray();
                        for (const row of existingRows) existingById.set(row.id, row);
                    }

                    for (const t of transactions) {
                        const merchantKey = normalizeMerchant(t.party);
                        // Prefer UTR / txn id when present so re-uploads never double-count
                        const stableKey = t.utr_no || t.transaction_id || t.txn_id
                            || `${t.date_time}_${t.party}_${t.amount}`;
                        const id = String(stableKey);
                        const existing = existingById.get(id);
                        const keepUserTag = existing
                            && (existing.category_source === 'user' || existing.category_source === 'manual');

                        let category;
                        let subcategory;
                        let confidence;
                        let category_source;

                        if (keepUserTag) {
                            category = existing.category;
                            subcategory = existing.subcategory;
                            confidence = existing.confidence ?? 1;
                            category_source = existing.category_source;
                        } else {
                            const catRes = await categorizeMerchant(t.party, userId, t.amount, t.type);
                            category = catRes.category;
                            subcategory = catRes.subcategory;
                            confidence = catRes.confidence;
                            category_source = catRes.source;
                            if (catRes.source === 'user') autoTagged += 1;
                        }

                        dbTxns.push({
                            id,
                            user_id: userId,
                            date: parseDateOnly(t.date_time)?.toISOString().split('T')[0] || t.date_time,
                            date_time: t.date_time,
                            amount: t.amount,
                            party: t.party,
                            type: t.type,
                            raw_merchant: t.party,
                            merchant_key: merchantKey,
                            category,
                            subcategory,
                            source: source,
                            confidence,
                            category_source,
                            account: t.account || '',
                            utr_no: t.utr_no || '',
                            transaction_id: t.transaction_id || t.txn_id || ''
                        });
                    }

                    // Diff against existing IDs for merge stats
                    const existingSet = new Set(existingById.keys());
                    let added = 0;
                    let updated = 0;
                    for (const t of dbTxns) {
                        if (existingSet.has(t.id)) updated += 1;
                        else added += 1;
                    }

                    if (wipeSource) {
                        await db.transactions.where('source').equals(source).delete();
                        added = dbTxns.length;
                        updated = 0;
                    }

                    if (dbTxns.length > 0) {
                        await db.transactions.bulkPut(dbTxns);
                    }

                    const sourceName = source === 'phonepe' ? 'PhonePe' : (source === 'gpay' ? 'GPay' : 'Paytm');
                    const stats = {
                        source,
                        sourceName,
                        added,
                        updated,
                        skippedOlder,
                        autoTagged,
                        total: dbTxns.length,
                        merged: !wipeSource,
                        proHistory: allowHistory,
                        at: Date.now(),
                    };
                    setLastUploadStats(stats);

                    let successText = wipeSource
                        ? `Loaded ${dbTxns.length} ${sourceName} transactions.`
                        : `Added ${added} new · ${updated} already saved · totals stay accurate (${sourceName}).`;
                    if (allowHistory && isFreshStart) {
                        successText = `Pro kept your history · merged ${added} new · ${updated} already saved (${sourceName}).`;
                    }
                    if (autoTagged > 0) successText += ` ${autoTagged} auto-tagged from your rules.`;
                    if (skippedOlder > 0) successText += ` (${skippedOlder} older than 30 days skipped on Free)`;

                    setStatus({ active: true, text: successText, type: 'success' });

                    // First parse → Reality Check; returning merge stays on app
                    const realityDone = localStorage.getItem('spenderly_reality_done') === '1';
                    if (!realityDone) {
                        setAppScreen('reality');
                        setActiveTab('home');
                    }

                    resolve(stats);
                }).catch(function (err) {
                    console.error(err);
                    setStatus({ active: true, text: 'Error: ' + (err.message || err), type: 'error' });
                    reject(err);
                });
            };

            fileReader.readAsArrayBuffer(file);
        });
    }, [user]);


    // ═══════════════════════════════════════════
    //  TRANSACTION ACTIONS
    // ═══════════════════════════════════════════

    /** Re-tag a transaction's category; returns count of rows updated */
    const retagTransaction = useCallback(async (txnIdx, newCategory, newSubcategory, applyAll) => {
        const txn = filteredTransactions[txnIdx];
        if (!txn) return 0;

        const userId = user ? user.id : 'guest-user';
        const updated = await handleRetag({
            userId,
            merchantKey: txn.merchant_key,
            newCategory,
            newSubcategory,
            applyToAll: applyAll,
            txnId: txn.id
        });
        return updated;
    }, [filteredTransactions, user]);

    /** Add a manually-entered transaction */
    const addManualTransaction = useCallback(async (t) => {
        const userId = user ? user.id : 'guest-user';
        const merchantKey = normalizeMerchant(t.party);
        const id = `${t.date_time}_${t.party}_${t.amount}_manual`;

        const txn = {
            id,
            user_id: userId,
            date: parseDateOnly(t.date_time)?.toISOString().split('T')[0] || t.date_time,
            date_time: t.date_time,
            amount: t.amount,
            party: t.party,
            type: t.type,
            raw_merchant: t.party,
            merchant_key: merchantKey,
            category: t.category || 'Uncategorized',
            subcategory: t.subcategory || 'Unknown',
            source: 'manual',
            confidence: 1.0,
            category_source: 'manual'
        };

        await db.transactions.put(txn);
    }, [user]);

    /** Clear transactions only — remembered merchants / goals / budgets stay */
    const clearAllTransactions = useCallback(async () => {
        await db.transactions.clear();
    }, []);

    /** Remove payments for selected sources only (phonepe | gpay | paytm | manual). */
    const clearTransactionsBySources = useCallback(async (sources) => {
        const list = (sources || []).filter(Boolean);
        if (!list.length) return 0;
        return db.transactions.where('source').anyOf(list).delete();
    }, []);

    /** Full wipe including overrides (dangerous — Pro should export vault first) */
    const wipeAllLocalData = useCallback(async () => {
        await Promise.all([
            db.transactions.clear(),
            db.user_overrides.clear(),
            db.merchant_registry.clear(),
            db.fixed_expenses.clear(),
            db.budgets.clear(),
            db.goals.clear(),
        ]);
    }, []);


    // ═══════════════════════════════════════════
    //  SAMPLE DATA — For demo mode
    // ═══════════════════════════════════════════

    /** Load pre-built sample transactions for demo — purely in-memory, never writes to IndexedDB */
    const loadSampleData = useCallback(() => {
        try {
            const userId = 'demo-user';
            setSampleTxns(SAMPLE_TRANSACTIONS.map(t => ({ ...t, user_id: userId })));
            setSampleFixedExpenses(SAMPLE_FIXED_EXPENSES);
            setSampleBudgets(SAMPLE_BUDGETS);
            setSampleGoals(SAMPLE_GOALS);
            setSampleIncome(SAMPLE_INCOME);
            setIsSampleMode(true);
            setStatus({ active: true, text: 'Sample data loaded — explore the full experience!', type: 'success' });
            setActiveTab('home');
            setAppScreen('app');
            localStorage.setItem('spenderly_reality_done', '1');
            localStorage.setItem('spenderly_leak_targets_set', '1');
            if (SAMPLE_INCOME) localStorage.setItem('spenderly_monthly_income', String(SAMPLE_INCOME));
        } catch (e) {
            console.error('Failed to load sample data:', e);
            setStatus({ active: true, text: 'Failed to load sample data: ' + e.message, type: 'error' });
        }
    }, []);

    /** Exit sample mode — clears in-memory state, real IndexedDB is untouched */
    const clearSampleData = useCallback(() => {
        setSampleTxns([]);
        setSampleFixedExpenses([]);
        setSampleBudgets([]);
        setSampleGoals([]);
        setSampleIncome(null);
        setIsSampleMode(false);
    }, []);


    // ═══════════════════════════════════════════
    //  FILTER ACTIONS
    // ═══════════════════════════════════════════

    const applyFilters = useCallback((newFilters) => {
        if (newFilters) setFilters(newFilters);
    }, []);

    const resetFilters = useCallback(() => {
        setFilters({ source: 'all', fromDate: '', toDate: '', type: 'all', merchantQuery: '' });
    }, []);


    // ═══════════════════════════════════════════
    //  PROVIDE ALL STATE TO CHILD COMPONENTS
    // ═══════════════════════════════════════════
    const value = {
        // Data — conditional on sample vs real mode
        allTransactions,
        filteredTransactions,
        monthFilteredTransactions,
        availableMonths,

        // Sample mode data (for views using useLiveQuery directly)
        isSampleMode,
        sampleFixedExpenses,
        sampleBudgets,
        sampleGoals,
        sampleIncome,

        // Sample mode lock modal
        showSampleLocked, setShowSampleLocked, triggerSampleLocked,

        // UI State
        activeTab, setActiveTab,
        theme, toggleTheme,
        status, setStatus,
        filters, setFilters,
        globalMonthFilter, setGlobalMonthFilter,
        passwordRequest, setPasswordRequest,
        lastUploadStats, setLastUploadStats,
        appScreen, setAppScreen,
        spendFocusCategory, setSpendFocusCategory,

        // Auth
        user, handleLogin, handleLogout,

        // Actions
        handleFile,
        applyFilters, resetFilters,
        retagTransaction,
        addManualTransaction,
        clearAllTransactions,
        clearTransactionsBySources,
        wipeAllLocalData,
        loadSampleData, clearSampleData,
    };

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
}
